import { useCallback } from 'react';
import { ChargeDto } from '@tiendanube/common';
import { BILLING_UPSELL_DISCOUNT } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { ChargeDisplayInterface } from '../pages/components/PaymentDataContent/PaymentDataContent';

export default function useGetTotalForCharges() {
  const [hasUpsellDiscountTag] = useHasTags(BILLING_UPSELL_DISCOUNT);

  const isPeriodDiscount = useCallback(
    (type: string | undefined) => {
      if (hasUpsellDiscountTag && type === 'store_discount-one_use')
        return false;

      return (
        type?.includes('period') ||
        type?.includes('one_use') ||
        type?.includes('onetimer')
      );
    },
    [hasUpsellDiscountTag],
  );

  const getChargeDiscountPercentage = useCallback(
    (
      charge: Pick<ChargeDto, 'amountBaseValue' | 'appliedDiscounts'>,
      withPeriodDiscounts: boolean,
    ): number => {
      if (charge.amountBaseValue === 0) {
        return 0;
      }

      const totalDiscount =
        charge.appliedDiscounts?.reduce(
          (acc, discount) =>
            withPeriodDiscounts || !isPeriodDiscount(discount.type)
              ? acc + discount.amountValue
              : acc,
          0,
        ) ?? 0;

      return Math.round((totalDiscount / charge.amountBaseValue) * 100);
    },
    [isPeriodDiscount],
  );

  const getChargeAmountWithGeneralDiscount = useCallback(
    (charge: ChargeDisplayInterface) => {
      const generalDiscountsTotal =
        charge.appliedDiscounts?.reduce(
          (acc, { amountValue, type }) =>
            !isPeriodDiscount(type) ? acc + Number(amountValue) : acc,
          0,
        ) ?? 0;

      return Number(charge.amountBaseValue) - generalDiscountsTotal;
    },
    [isPeriodDiscount],
  );

  const getTotalForCharges = useCallback(
    (
      charges: ChargeDisplayInterface[],
      {
        includeBaseAmount = true,
        includeGeneralDiscounts,
        includePeriodDiscounts,
        includeTaxes,
      }: {
        includeBaseAmount?: boolean;
        includeGeneralDiscounts: boolean;
        includePeriodDiscounts: boolean;
        includeTaxes: boolean;
      },
    ): number =>
      charges.reduce((acc, charge) => {
        if (includeBaseAmount) {
          acc += Number(charge.amountBaseValue);
        }

        if (includePeriodDiscounts) {
          acc -=
            charge.appliedDiscounts?.reduce(
              (acc, { amountValue, type }) =>
                isPeriodDiscount(type) ? acc + Number(amountValue) : acc,
              0,
            ) ?? 0;
        }
        if (includeGeneralDiscounts) {
          acc -=
            charge.appliedDiscounts?.reduce(
              (acc, { amountValue, type }) =>
                !isPeriodDiscount(type) ? acc + Number(amountValue) : acc,
              0,
            ) ?? 0;
        }
        if (includeTaxes) {
          acc +=
            charge.taxes?.reduce(
              (acc, tax) => acc + Number(tax.amountValue),
              0,
            ) ?? 0;
        }
        return acc;
      }, 0),
    [isPeriodDiscount],
  );

  const getPriceByMonthWithDiscount = useCallback(
    (charge: ChargeDisplayInterface) => {
      const total = getTotalForCharges([charge], {
        includeGeneralDiscounts: true,
        includePeriodDiscounts: true,
        includeTaxes: false,
      });
      const months =
        charge.recurringInterval &&
        (charge.recurringFrequency === 'M'
          ? charge.recurringInterval
          : charge.recurringInterval / 30);
      if (!months) return undefined;
      return total / months;
    },
    [getTotalForCharges],
  );

  return {
    isPeriodDiscount,
    getChargeDiscountPercentage,
    getChargeAmountWithGeneralDiscount,
    getTotalForCharges,
    getPriceByMonthWithDiscount,
  };
}
